/**
 * API Service for making requests to Cloudflare backend
 * Uses Firebase ID tokens for authentication
 */

const BASE_URL = '/api'

// Get Firebase auth token from memory (set by auth.js)
let authToken = null

// Set auth token (called by auth.js after Firebase login)
export function setAuthToken(token) {
  authToken = token
}

// Get current auth token
export function getAuthToken() {
  return authToken
}

async function tryRefreshAccessToken() {
  try {
    const fingerprintRaw = localStorage.getItem('device_fingerprint')
    if (!fingerprintRaw) return false

    let fingerprint
    try {
      fingerprint = JSON.parse(fingerprintRaw)
    } catch {
      return false
    }

    const response = await fetch('/api/auth/refresh', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ deviceFingerprint: fingerprint })
    })

    if (!response.ok) return false

    const data = await response.json()
    if (!data?.accessToken) return false

    localStorage.setItem('access_token', data.accessToken)
    setAuthToken(data.accessToken)
    return true
  } catch (error) {
    console.error('Refresh attempt failed:', error)
    return false
  }
}

function clearAuthAndRedirect() {
  setAuthToken(null)
  localStorage.removeItem('google_user')
  localStorage.removeItem('google_token')
  localStorage.removeItem('access_token')
  window.location.href = '/login'
}

// Base fetch wrapper
async function request(endpoint, options = {}) {
  const token = getAuthToken()
  
  // Check if token is expired before making request
  if (token) {
    try {
      // Only validate JWT tokens (Google tokens), not custom access tokens
      // Custom access tokens are 64 char hex strings, not JWTs
      if (token.length !== 64) {
        const tokenData = parseJwt(token)
        const expiryTime = tokenData.exp * 1000
        const currentTime = Date.now()
        
        if (currentTime >= expiryTime) {
          console.warn('Token expired, redirecting to login')
          clearAuthAndRedirect()
          throw new Error('Token expired')
        }
      }
      // Custom access tokens don't expire on client side - backend validates them
    } catch (e) {
      console.error('Token validation error:', e)
    }
  }
  
  const headers = {
    'Content-Type': 'application/json',
    ...options.headers
  }
  
  // Add Firebase ID token to Authorization header
  if (token) {
    headers['Authorization'] = `Bearer ${token}`
  }
  
  const config = {
    ...options,
    headers
  }
  
  try {
    const response = await fetch(`${BASE_URL}${endpoint}`, config)
    
    // Handle 401 unauthorized - token expired or invalid
    if (response.status === 401) {
      console.warn('Unauthorized request - token may be expired, trying refresh')

      const refreshed = await tryRefreshAccessToken()
      if (refreshed) {
        const retryHeaders = {
          ...headers,
          Authorization: `Bearer ${getAuthToken()}`
        }

        const retryResponse = await fetch(`${BASE_URL}${endpoint}`, {
          ...config,
          headers: retryHeaders
        })

        if (retryResponse.status === 401) {
          console.warn('Retry after refresh still unauthorized')
          clearAuthAndRedirect()
          throw new Error('Unauthorized')
        }

        const retryData = await retryResponse.json()
        if (!retryResponse.ok) {
          throw new Error(retryData.error || 'Request failed')
        }

        return retryData
      }

      clearAuthAndRedirect()
      throw new Error('Unauthorized')
    }
    
    const data = await response.json()
    
    if (!response.ok) {
      throw new Error(data.error || 'Request failed')
    }
    
    return data
  } catch (error) {
    console.error('API Error:', error)
    throw error
  }
}

// Parse JWT helper
function parseJwt(token) {
  const base64Url = token.split('.')[1]
  const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/')
  const jsonPayload = decodeURIComponent(
    atob(base64)
      .split('')
      .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
      .join('')
  )
  return JSON.parse(jsonPayload)
}

// API methods
export const api = {
  // GET app version (no auth required)
  async getAppVersion() {
    try {
      const response = await fetch(`${BASE_URL}/version`)
      return await response.json()
    } catch (error) {
      console.error('Failed to get app version:', error)
      return { version: 0 }
    }
  },

  // Admin: Check if current user is admin
  async checkAdmin() {
    return request('/admin/check', { method: 'GET' })
  },

  // Admin: Force app refresh for all users
  async forceAppRefresh() {
    return request('/admin/force-refresh', { method: 'POST' })
  },

  // Admin: Backup database
  async backupDatabase() {
    const token = getAuthToken()
    const response = await fetch(`${BASE_URL}/admin/backup-db`, {
      headers: { 'Authorization': `Bearer ${token}` }
    })
    if (!response.ok) throw new Error('Backup failed')
    return response.blob()
  },

  // Admin: Restore database
  async restoreDatabase(file) {
    const token = getAuthToken()
    const text = await file.text()
    const response = await fetch(`${BASE_URL}/admin/restore-db`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: text
    })
    return await response.json()
  },

  // Admin: View database tables
  async viewDatabaseTables() {
    return request('/admin/view-tables', { method: 'GET' })
  },

  // Admin: View table data
  async viewTableData(tableName, limit = 100, offset = 0) {
    return request(`/admin/view-table-data?table=${encodeURIComponent(tableName)}&limit=${limit}&offset=${offset}`, { method: 'GET' })
  },

  // Admin: Get user statistics
  async getUserStats() {
    return request('/admin/user-stats', { method: 'GET' })
  },
  
  // GET request
  async get(endpoint) {
    return request(endpoint, { method: 'GET' })
  },
  
  // POST request
  async post(endpoint, data) {
    return request(endpoint, {
      method: 'POST',
      body: JSON.stringify(data)
    })
  },
  
  // PUT request
  async put(endpoint, data) {
    return request(endpoint, {
      method: 'PUT',
      body: JSON.stringify(data)
    })
  },
  
  // DELETE request
  async delete(endpoint) {
    return request(endpoint, { method: 'DELETE' })
  }
}
